import {
  Channel,
  GetOrCreateUserInterface,
  OnlineRosterEntry,
  SessionInterface,
  StrengthenIdentityProof,
  StrengthenIdentityResult,
  UserInterface,
  UserPropKey,
  UserVarKey
} from "@swivl-mono/types";

import { APIService } from "../Service/API.Service";

//LINK - apps/server/src/API/Users/Users.Controller.ts

export class UsersAPI {
  static async userNeedsMergeValidation(orgId: number, userId: number, phoneNumber: string) {
    return APIService.Server.get<boolean>(`/users/org/${orgId}/user/${userId}/user-needs-merge-validation`, { phoneNumber });
  }

  static async mergeUser(orgId: number, userId: number, phoneNumber: string) {
    return APIService.Server.get<UserInterface | undefined>(`/users/org/${orgId}/user/${userId}/merge`, { phoneNumber });
  }

  static async mergeUserAndSession(orgId: number, userId: number, phoneNumber: string, currentSessionId: number, channel: Channel) {
    return APIService.Server.post<{ user: UserInterface; session: SessionInterface }>(`/users/org/${orgId}/user/${userId}/merge-user-and-session`, {
      userPhoneNumber: phoneNumber,
      currentSessionId,
      channel
    });
  }

  /**
   * V2 identity-strengthening — single entrypoint for every proof type.
   * Returns the canonical (surviving) user id. See
   * docs/snowy/widget/2026-05-21-user-identity-and-merge.md §2.2.
   */
  static async strengthenIdentity(orgId: number, conversationUserId: number, proof: StrengthenIdentityProof) {
    return APIService.Server.post<StrengthenIdentityResult>(`/users/org/${orgId}/user/${conversationUserId}/strengthen-identity`, { proof });
  }

  /** ACCESS path: find the account user owning the proof (phone) WITHOUT merging.
   *  Returns the account user, or null when no separate account owns it. */
  static async findAccountByProof(orgId: number, conversationUserId: number, proof: StrengthenIdentityProof) {
    return APIService.Server.post<UserInterface | null>(`/users/org/${orgId}/user/${conversationUserId}/find-account-by-proof`, { proof });
  }

  static async getUserById(userId: number) {
    return APIService.Server.get<UserInterface>(`/users/user/${userId}`);
  }

  static async getUserToken(orgId: number, userId: number) {
    return APIService.Server.get<string>(`/users/org/${orgId}/user/${userId}/token`);
  }

  static async getUserByIdAndToken(userId: number, token: string) {
    return APIService.Server.get<UserInterface>(`/users/user/${userId}/token/${token}`);
  }

  static async getUsersByVar(orgId: number, userVarKey: UserVarKey | UserPropKey, value: string) {
    return APIService.Server.get<UserInterface[]>(`/users/org/${orgId}/user/var/${userVarKey}/${value}`);
  }

  static async searchForUsers(orgId: number, searchTerm: string, take?: number, channel?: Channel) {
    let query: any = { searchTerm };
    if (take) {
      query.take = take;
    }
    if (channel) {
      query.channel = channel;
    }
    return APIService.Server.get<UserInterface[]>(`/users/org/${orgId}/user/search`, query);
  }

  static async getUsersByPhone(orgId: number, phone?: string) {
    return APIService.Server.post<UserInterface[]>(`/users/org/${orgId}/user/contact-info`, {
      phone
    });
  }

  // static async getUsersFromPhoneNumbers(orgId: number, phone?: string) {
  //   return APIService.Server.get<UserInterface[]>(`/users/org/${orgId}/users/from-phone-numbers`, { phone });
  // }
  static async createUser(
    orgId: number,
    user: Partial<UserInterface> | undefined,
    userVars: { [key: string]: string } | undefined,
    createdBy: string = ""
  ) {
    return APIService.Server.post<UserInterface>(
      `/users/org/${orgId}/user`,
      {
        user,
        userVars
      },
      { createdBy: createdBy }
    );
  }

  static async updateUser(orgId: number, userId: number, user: Partial<UserInterface>) {
    return APIService.Server.put<UserInterface>(`/users/org/${orgId}/user/${userId}`, user);
  }

  static async getOrCreateUserByPhoneNumber(orgId: number, phoneNumber: string, createdBy?: string) {
    const params: any = { phoneNumber };
    if (createdBy && createdBy.trim()) {
      params.createdBy = createdBy;
    }
    return APIService.Server.get<GetOrCreateUserInterface>(`/users/org/${orgId}/user`, params);
  }

  static async getUsersById(orgId: number, userIds: number[]): Promise<UserInterface[]> {
    // throw new Error('Method not implemented.');
    return APIService.Server.get<UserInterface[]>(`/users/org/${orgId}/users-by-id`, {
      userIds: userIds
    });
  }

  static async getRecentlyActiveUsers(orgId: number, take: number, skip: number): Promise<UserInterface[]> {
    // throw new Error('Method not implemented.');
    return APIService.Server.get<UserInterface[]>(`/users/org/${orgId}/recently-active`, {
      take: take,
      skip: skip
    });
  }

  static async renewTranscriptAccess(orgId: number, userId: number) {
    return APIService.Server.put<boolean>(`/users/org/${orgId}/user/${userId}/renew-transcript-access`);
  }

  static async setArchivedForUser(orgId: number, userId: number, archived: boolean): Promise<UserInterface> {
    return APIService.Server.put<UserInterface>(`/users/org/${orgId}/user/${userId}/set-archive/${archived}`);
  }

  static async getOnlineUsers(orgId: number) {
    return APIService.Server.get<{ [userId: number]: OnlineRosterEntry }>(`/users/org/${orgId}/online`);
  }
}
